import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeStyle, SafeUrl } from '@angular/platform-browser';
import { detect } from 'detect-browser';
import exifr from 'exifr/dist/lite.esm';
import * as semver from 'semver';

/**
 * The minimum browser versions needed to use the built in
 * image-orientation and ignore our JS exif rotation
 */
const ORIENTATION_MIN_VERSION = {
  chrome: '81.0.0',
  safari: '13.1.0',
  'edge-chromium': '81.0.0',
  firefox: '77.0.0',
};

const DEFAULT_PRIMARY = '#2B2B2B';
const DEFAULT_SECONDARY = '#2B2B2B';

/**
 * Image loader component provides options for the loading state of an image:
 * 1. Styled by the host (Set background-image for ca-image-loader)
 * 2. Gradient (showPlaceholder must be true, background must be false)
 * 3. CareApp Mofif (a flower image, showPlaceholder, background must be true)
 * 4. Blurhash (blur input must exist. This will always take priority)
 */

@Component({
    selector: 'ca-image-loader',
    templateUrl: './image-loader.component.html',
    styleUrls: ['./image-loader.component.scss'],
    standalone: false
})
export class ImageLoaderComponent implements AfterContentInit {
  get image() {
    return this._image;
  }

  @Input()
  set image(data) {
    let newImage;
    if (!data) {
      newImage = false;
    } else {
      if (Array.isArray(data)) {
        newImage = data[0];
      } else {
        newImage = data;
      }
    }
    if (newImage !== this._image) {
      this._image = newImage;

      // If it's a data URL (from FileReader), we can skip the loading state
      if (typeof this._image === 'string' && this._image.startsWith('data:')) {
        this.imageLoaded = true;
      } else {
        this.imageLoaded = false;
      }

      this.imageSrc = this.sanitize.bypassSecurityTrustUrl(this._image.toString());
    }
  }

  @Input()
  set blur(data) {
    let newBlur = data ?? false;
    if (!!newBlur && newBlur !== this._blur) {
      this._blur = newBlur;
    }
  }

  get blur(): string | false {
    return this._blur;
  }

  @HostBinding('style.background-size')
  get backgroundSize() {
    if (this.blur) {
      return 'cover';
    }
    return this.fit ?? '';
  }

  @HostBinding('style.background-image')
  get hostBackground() {
    if (this.blur) {
      return `url(${this.blur})`;
    } else if (this.background) {
      return this.gradient;
    }
    return '';
  }

  get blurSrc() {
    if (this.blur) {
      return this.sanitize.bypassSecurityTrustStyle(this.blur);
    }
    return null;
  }

  get localBackground() {
    let chosenBackground = '';
    // For data URLs or loaded images, show the background
    if (
      this.image &&
      (this.imageLoaded || (typeof this.image === 'string' && this.image.startsWith('data:')))
    ) {
      chosenBackground = `url(${this.image})`;
    } else {
      if (this.showPlaceholder && !this.background) {
        chosenBackground = this.gradient;
      }
    }
    return this.sanitize.bypassSecurityTrustStyle(chosenBackground);
  }

  get gradient() {
    return this.showPlaceholder
      ? `linear-gradient(to top right, ${this.primary || DEFAULT_PRIMARY}, ${
          this.secondary || DEFAULT_SECONDARY
        })`
      : 'transparent';
  }

  constructor(private sanitize: DomSanitizer, private cd: ChangeDetectorRef) {}

  static browser = detect();
  _image: string | false;

  transform: SafeStyle;

  imageSrc: SafeUrl;

  _blur: string | false;

  @Input() autoRotate = true;
  // scale that is applied if we need to rotate
  @Input() rotationScale = '1';

  @Input()
  showPlaceholder = true;

  @Input()
  fit = 'cover';
  @Input()
  filter = '';

  @Input()
  title = '';
  @HostBinding('class.has-background')

  /**
   * Whether or not to show the CareApp motif as placholder.
   * Set to false if you are styling the image-loader from another component's styles.
   */
  @Input()
  background = true;

  imageLoaded = false;

  @ViewChild('imgEl')
  imgEl: ElementRef;

  @HostBinding('class.init')
  init = false;

  primary: string = DEFAULT_PRIMARY;
  secondary: string = DEFAULT_SECONDARY;

  ngAfterContentInit() {
    if (this.imageLoaded) {
      return;
    }
    this.init = true;
  }

  async setImageLoaded() {
    const version = ImageLoaderComponent.browser.version;
    const name = ImageLoaderComponent.browser.name;
    // if the browser is one that supports automatic orientation, do nothing
    if (ORIENTATION_MIN_VERSION[name] && semver.gte(version, ORIENTATION_MIN_VERSION[name])) {
      this.imageLoaded = true;
      // if we need to autorotate with javascript, do it
    } else if (this.autoRotate) {
      try {
        const rawTransform = await this.getTransform(this.imgEl.nativeElement);
        this.transform = this.sanitize.bypassSecurityTrustStyle(rawTransform);
      } catch (error) {
        // If Exifr doesn't recognise the format, passively fail the transform attempt.
        console.error(error);
      }
      this.imageLoaded = true;
    } else {
      this.imageLoaded = true;
    }
    this.cd.markForCheck();
  }

  getTransform(imgElement): Promise<any> {
    return exifr.orientation(imgElement).then((orientation) => {
      switch (orientation) {
        case 6:
          return `rotate(90deg) scale(${this.rotationScale})`;
        case 3:
          return `rotate(180deg)`;
        case 8:
          return `rotate(270deg) scale(${this.rotationScale})`;
        default:
          return '';
      }
    });
  }
}
